import { KalturaSubscriptionDependencyType } from '@kaltura-ott/ott-client-types';
import { KalturaEntitlement, KalturaPurchaseStatus, KalturaRuleType } from '@kaltura-ott/ott-client-types/api/types';
import {
  getSubscriptionsList,
  getUserRules,
  KuxFilteredCategoriesDTO,
  KuxPpv,
  MediaType,
  PurchaseErrors,
  PurchaseStates,
  setPurchaseCache,
  transactionsPurchase,
} from '@kaltura-ott/tvpil';
import { action, computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container, singleton } from 'tsyringe';

import { featureEnabler, filterRules } from '../../utils';
import { assertPresence, errorHandling } from '../../utils/error-handling';
import type { AppParametersStore } from '../app/app-params-store';
import type { AppStore } from '../app/app-store';
import {
  addProviderPlanToSubscriptions,
  cancelSubscriptions,
  getPaymentMethodList,
  getBaseSubscriptionCollection,
  getSubscriptionCollection,
  getSubscriptionsData,
  isTypePurchased,
  SubscriptionItem,
  getSubscriptionEntitlements,
} from '../category/subpage/subscription/subscription-services';
import { Features } from '../config';
import type { DmsStore } from '../dms/dms-store';
import { Package } from '../entities/Package';
import { createPackageEntity, createSubscriptionEntity } from '../entities/subsciption.service';
import type { Subscription } from '../entities/Subscription';
import { ErrorPriority } from '../error';
import type { LinearRepository } from '../linearRepository/linear-repository';
import { ProvidersRepository } from '../providersRepository';
import { SubscriptionsRepository } from '../subscriptionsRepository';
import type { UserStore } from '../user/';
import type { LoginStore } from '../user/login-store';

export interface PurchaseRentParams {
  mediaFileId: number;
  ppv: KuxPpv;
  assetId: number;
}

export interface SubscriptionProps {
  subscriptionId: string;
  currency: string;
  price: number;
}

export type TransactionResult =
  | { state: PurchaseStates.enterPin }
  | { state: PurchaseStates.ok }
  | { state: PurchaseStates.error; code: PurchaseErrors.accessTokenNotPassed }
  | { state: PurchaseStates.error; code: string }
  | { state: PurchaseStates.redirect; redirectUrl: string }
  | { state: PurchaseStates.pending }
  | { state: PurchaseStates.geoBlock };

@singleton()
export class TransactionStore {
  static moduleName = 'TransactionStore';

  @observable subscriptions: Subscription[] = [];
  @observable offers: Subscription[] = [];
  @observable heroes: Subscription[] = [];
  @observable additionalBasePlans: Subscription[] = [];
  @observable additionPlans: Subscription[] = [];

  @observable subscriptionsReady = false;
  @observable isPurchaseAllowed = true;
  @observable isSubscriptionPurchaseAllowed = true;
  @observable isPpvPurchaseAllowed = true;
  @observable lastDigits = '';

  public paymentMethodId?: number;

  private lastInitSubscriptionTime?: number;
  private subscriptionsList?: SubscriptionItem[];

  @observable.ref private subscriptionIds: string[] = [];
  subpageProperty?: KuxFilteredCategoriesDTO['subpageProperty'];

  get subscriptionsRepository() {
    return container.resolve(SubscriptionsRepository);
  }

  private get providersRepository() {
    return container.resolve<ProvidersRepository>('providersRepository');
  }

  async init(): Promise<void> {
    this.checkAllowingOnPurchase();

    await this.loadMainSubscriptions();
    const appStore = container.resolve<AppStore>('appStore');

    if (!appStore._tvpilParentalPinStore.originalStore.isInitiated) {
      appStore._tvpilParentalPinStore.originalStore.init();
    }
  }

  /**
   * loads subscriptions and populates
   * @observables subscriptions, offers, heroes
   *
   */

  @action
  @errorHandling({ type: ErrorPriority.high })
  async initSubscriptions(): Promise<void> {
    const loginStore = container.resolve<LoginStore>('loginStore');

    if (loginStore.isAnonymous) {
      return;
    }

    this.subscriptionsReady = false;

    await this.loadMainSubscriptions();
    await this.loadSubscriptions();

    this.resetStore();
    this.providersRepository.calculatePurchasedProviders(this.subscriptions);
    this.providersRepository.calculateAvailableProviders();

    this.addAdditionPlans();

    this.subscriptionsReady = true;
  }

  async checkFFSubscriptions() {
    const dmsStore = container.resolve<DmsStore>('dmsStore');

    if (Date.now() - (this.lastInitSubscriptionTime || 0) > dmsStore.dms.minFFEntitlementCheckIntervalSec * 1000) {
      await this.loadMainSubscriptions();
    }
  }

  async loadSets(): Promise<void> {
    await this.subscriptionsRepository.loadSubscriptionSets();
  }

  async loadMainSubscriptions() {
    const loginStore = container.resolve<LoginStore>('loginStore');
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    const userStore = container.resolve<UserStore>('userStore');

    if (loginStore.isAnonymous) {
      return;
    }

    const promises = [];

    if (featureEnabler.variation(Features.CREDIT_CARD_INDICATOR, false) && userStore.customerType === '1') {
      promises.push(this.paymentMethodList());
    } else {
      this.lastDigits = '';
      this.paymentMethodId = undefined;
    }

    promises.push(this.loadSets());
    promises.push(
      (async () => {
        const entitlements = await getSubscriptionEntitlements();
        this.subscriptionIds = entitlements?.map((entitlement: KalturaEntitlement) => entitlement.productId);

        this.subscriptionsList = await getSubscriptionsData({
          assetType: dmsStore?.dms?.mediaTypes[MediaType.Package],
          subscriptionIds: this.subscriptionIds,
          entitlements,
        });

        this.subscriptions = this.subscriptionsList
          .map((item) => {
            if (item.asset) {
              return createSubscriptionEntity(item.subscription, createPackageEntity(item.asset), item.purchaseInfo);
            }
          })
          .filter(Boolean) as Subscription[];

        this.lastInitSubscriptionTime = Date.now();
      })(),
    );

    await Promise.all(promises);
  }

  private async loadSubscriptions(): Promise<void> {
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    const userStore = container.resolve<UserStore>('userStore');

    const dms = assertPresence(dmsStore.dms);
    const sets = this.subscriptionsRepository.sets;

    const userCustomerType = userStore.customerType;

    let basePackageIds: number[] = [];
    let subscriptionExcludeIds: string[] = [];
    if (this.subscriptionsList && this.subscriptionsList.length > 0) {
      basePackageIds = this.subscriptionsList
        .filter(({ subscription }) => subscription.dependencyType === 'BASE')
        .map(({ subscription }) => Number(subscription.id));

      subscriptionExcludeIds = this.subscriptionsList
        .filter(({ subscription }) => !!subscription.id)
        .map(({ subscription }) => subscription.id);
    }

    const promises = [];
    const offers: Subscription[] = [];

    if (this.subpageProperty?.addonCollectionId) {
      promises.push(
        (async () => {
          const addons = await getSubscriptionCollection({
            // @ts-expect-error TODO Fix
            collectionId: this.subpageProperty.addonCollectionId,
            basePackageIds,
            subscriptionExcludeIds,
            userCustomerType,
            assetType: dms.mediaTypes[MediaType.Package],
            sets,
          });

          const addonEntities = addons
            .map((addon) => {
              return createSubscriptionEntity(
                addon.subscription,
                createPackageEntity(addon.assetPackage),
                addon.purchaseInfo,
              );
            })
            .filter((addon) => !addon.isPurchased);

          offers.push(...addonEntities);
        })(),
      );
    }

    if (this.subpageProperty?.notApplicableCollectionId) {
      promises.push(
        (async () => {
          const notApplicableSubscriptions = await getSubscriptionCollection({
            // @ts-expect-error TODO Fix
            collectionId: this.subpageProperty.notApplicableCollectionId,
            basePackageIds: [],
            subscriptionExcludeIds,
            userCustomerType,
            assetType: dms.mediaTypes[MediaType.Package],
            sets,
          });

          const notApplicableEntities = notApplicableSubscriptions.map((item) => {
            return createSubscriptionEntity(
              item.subscription,
              createPackageEntity(item.assetPackage),
              item.purchaseInfo,
            );
          });

          offers.push(...notApplicableEntities);
        })(),
      );
    }

    if (this.subpageProperty?.heroCollectionId) {
      promises.push(
        (async () => {
          const heroes = await getSubscriptionCollection({
            // @ts-expect-error TODO Fix
            collectionId: this.subpageProperty.heroCollectionId,
            basePackageIds,
            subscriptionExcludeIds,
            userCustomerType,
            assetType: dms.mediaTypes[MediaType.Package],
            sets,
          });

          this.heroes = heroes.slice(0, 1).map((hero) => {
            return createSubscriptionEntity(
              hero.subscription,
              createPackageEntity(hero.assetPackage),
              hero.purchaseInfo,
            );
          });
        })(),
      );
    }

    if (dmsStore.dms.temporaryParams.morePlanCollection) {
      promises.push(
        (async () => {
          const baseSubscriptionCollection = await getBaseSubscriptionCollection(
            Number(dmsStore.dms.temporaryParams.morePlanCollection),
            userStore?.userData?.dynamicData['customer_type'].value as string,
          );

          this.additionalBasePlans = baseSubscriptionCollection
            .map((subscription) => {
              if (
                dmsStore.dms.temporaryParams.filterLowerSubscription &&
                subscription.subscription.price.amount < this.plans[0]?.price.amount
              ) {
                return;
              }
              return createSubscriptionEntity(
                subscription.subscription,
                createPackageEntity(subscription.assetPackage),
                subscription.purchaseInfo,
              );
            })
            .filter(Boolean) as Subscription[];
        })(),
      );
    }

    await Promise.all(promises);

    this.offers = offers;
  }

  /**
   *
   * @param subscriptionProps - subscriptionId (id of subscription to purchase), currency, price - amount value
   * method shouldn't be called directly from the store. It is called on Subscription instance
   */

  @action
  @errorHandling()
  async purchaseSubscription(subscriptionProps: SubscriptionProps): Promise<TransactionResult> {
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    const userStore = container.resolve<UserStore>('userStore');
    const appStore = container.resolve<AppStore>('appStore');
    const linearRepository = container.resolve<LinearRepository>('linearRepository');

    await userStore.loadUserData();
    appStore._tvpilParentalPinStore.originalStore.purchasePinEntered = false;
    const result = await this.transactionPurchase(subscriptionProps);
    await linearRepository.init(dmsStore.dms.mediaTypes[MediaType.Linear]);
    return result;
  }
  /**
   *
   * @param subscriptionProps -- subscriptionId (id of subscription to purchase), currency, price - amount value
   * method shouldn't be called directly from the store. It is called on Subscription instance
   */

  async transactionPurchase(subscriptionProps: SubscriptionProps): Promise<TransactionResult> {
    const result = await this.transaction(subscriptionProps);
    if (result.state === PurchaseStates.ok) {
      await this.loadMainSubscriptions();
      await this.loadSubscriptions();
    }
    return result;
  }

  /**
   * TODO not used
   * @param subscriptionId - id of subscription to cancel
   * method shouldn't be called directly from the store. It is called on Subscription instance
   */

  @action
  @errorHandling()
  async cancelSubscription(subscriptionId: string) {
    const appStore = container.resolve<AppStore>('appStore');

    try {
      await cancelSubscriptions(subscriptionId);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return {
        state: PurchaseStates.error,
        message: error.message,
      };
    }

    await appStore?.resetWidgets({ rent: false });

    return {
      state: PurchaseStates.ok,
    };
  }

  /**
   *
   * @param params - mediaFileId of asset to purchase, ppv object: the selected rent on the details page
   * method shouldn't be called directly from the store. It is called on Ppv instance
   */

  @action
  @errorHandling()
  async purchaseRent(params: PurchaseRentParams): Promise<TransactionResult> {
    const userStore = container.resolve<UserStore>('userStore');
    const appStore = container.resolve<AppStore>('appStore');

    await userStore.loadUserData();
    appStore._tvpilParentalPinStore.originalStore.purchasePinEntered = false;
    return this.transactionRent(params);
  }

  /**
   *
   * @param purchaseRentParams: PurchaseRentParams
   * https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/2171769262/KUX+Payment+Router#Error-codes
   * method shouldn't be called directly from the store. It is called on Ppv instance
   */

  @action
  @errorHandling()
  async transactionRent(purchaseRentParams: PurchaseRentParams): Promise<TransactionResult> {
    return this.transaction(purchaseRentParams);
  }

  private getTransactionPpvInfo(purchaseParams: PurchaseRentParams) {
    const loginStore = container.resolve<LoginStore>('loginStore');
    const userStore = container.resolve<UserStore>('userStore');
    const appParametersStore = container.resolve<AppParametersStore>('appParamsStore');

    const userType = userStore?.userData?.dynamicData['customer_type'].value as string;
    const accessToken = loginStore?.jwtToken?.jwt as string;
    const householdExternalId = userStore?.userData?.externalId.toString() as string;
    return {
      mediaFileId: purchaseParams.mediaFileId,
      price: purchaseParams.ppv.price.amount,
      currency: purchaseParams.ppv.price?.currency,
      ppvModuleId: purchaseParams.ppv.id,
      partnerId: assertPresence(appParametersStore.params).dmsSetting.partnerId,
      userType,
      accessToken,
      householdExternalId,
      paymentMethodId: this.paymentMethodId,
    };
  }

  private async getTransactionSubscriptionInfo(purchaseParams: SubscriptionProps) {
    const loginStore = container.resolve<LoginStore>('loginStore');
    const userStore = container.resolve<UserStore>('userStore');
    const appParametersStore = container.resolve<AppParametersStore>('appParamsStore');

    const userType = userStore?.userData?.dynamicData['customer_type'].value as string;
    const accessToken = loginStore?.jwtToken?.jwt as string;
    const householdExternalId = userStore?.userData?.externalId.toString() as string;
    const subscription = (await lastValueFrom(getSubscriptionsList([purchaseParams.subscriptionId])))[0];

    return {
      subscriptionId: Number(purchaseParams.subscriptionId),
      currency: purchaseParams.currency,
      price: purchaseParams.price,
      partnerId: assertPresence(appParametersStore.params).dmsSetting.partnerId,
      userType,
      accessToken,
      householdExternalId,
      subscriptionType: subscription.dependencyType as KalturaSubscriptionDependencyType,
      paymentMethodId: this.paymentMethodId,
      subscriptionExternalId: subscription.externalId,
    };
  }

  private async checkGeoBlock(purchaseParams: PurchaseRentParams | SubscriptionProps): Promise<boolean> {
    if ('ppv' in purchaseParams) {
      const rules = await lastValueFrom(getUserRules(purchaseParams.assetId, 1));
      const geoBlockingRules = filterRules(rules, KalturaRuleType.geo);
      return !!geoBlockingRules.length;
    }

    return false;
  }

  private async transaction(
    purchaseParams: PurchaseRentParams | SubscriptionProps,
    refreshTimes = 0,
  ): Promise<TransactionResult> {
    const loginStore = container.resolve<LoginStore>('loginStore');
    const appStore = container.resolve<AppStore>('appStore');

    const isGeoCheckRequire = featureEnabler.variation('CHECK_GEO_BLOCKING_RENT_FLOW', false);
    if (isGeoCheckRequire) {
      const isGeoBlock = await this.checkGeoBlock(purchaseParams);
      if (isGeoBlock) {
        return {
          state: PurchaseStates.geoBlock,
        };
      }
    }
    const { pinForPurchase, purchasePinEntered } = appStore._tvpilParentalPinStore.originalStore;
    if (pinForPurchase && !purchasePinEntered) {
      return {
        state: PurchaseStates.enterPin,
      };
    }

    let endDate;
    if ('ppv' in purchaseParams) {
      endDate = Date.now() / 1000 + purchaseParams.ppv.viewLifeCycle * 60;
    }

    const result = await transactionsPurchase(
      'ppv' in purchaseParams
        ? this.getTransactionPpvInfo(purchaseParams)
        : await this.getTransactionSubscriptionInfo(purchaseParams),
    );

    if (result.state === PurchaseStates.ok) {
      await appStore?.resetWidgets({ rent: 'ppv' in purchaseParams });
    }

    setPurchaseCache({
      purchaseState: result.state,
      ...('mediaFileId' in purchaseParams
        ? { mediaFileId: purchaseParams.mediaFileId, endDate: endDate }
        : { subscriptionId: purchaseParams.subscriptionId }),
    });

    switch (result.state) {
      case PurchaseStates.ok:
        return { state: PurchaseStates.ok };
      case PurchaseStates.redirect:
        return {
          state: PurchaseStates.redirect,
          redirectUrl: result.id,
        };
      case PurchaseStates.pending:
        return {
          state: PurchaseStates.pending,
        };
      case PurchaseStates.error:
        if (result.code === PurchaseErrors.accessTokenNotPassed) {
          loginStore.logoutUser(true, false);
        }

        if (result.code === PurchaseErrors.accessTokenInvalid && refreshTimes === 0) {
          try {
            refreshTimes += 1;

            await loginStore.updateAccessToken();
          } catch (e) {
            return {
              state: PurchaseStates.error,
              code: PurchaseErrors.accessTokenInvalid,
            };
          }

          return this.transaction(purchaseParams, refreshTimes);
        }

        return {
          state: PurchaseStates.error,
          code: result.code,
        };

      default:
        return {
          state: PurchaseStates.error,
          code: '1',
        };
    }
  }

  private checkAllowingOnPurchase() {
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    const appStore = container.resolve<AppStore>('appStore');
    const appParametersStore = container.resolve<AppParametersStore>('appParamsStore');

    const currentDeviceBrand = assertPresence(appParametersStore.currentDevice.brandId).toString();
    const deniedList = appStore?.rootStore?.categoryTree.subpageProperty.purchaseDeviceBrandDenialList ?? [];
    const subscriptionDeniedList = dmsStore.dms.temporaryParams.subscriptionDenialList || [];
    const ppvDeniedList = dmsStore.dms.temporaryParams.ppvDenialList || [];

    this.isPurchaseAllowed = !deniedList.includes(currentDeviceBrand);
    this.isSubscriptionPurchaseAllowed = this.isPurchaseAllowed && !subscriptionDeniedList.includes(currentDeviceBrand);
    this.isPpvPurchaseAllowed = this.isPurchaseAllowed && !ppvDeniedList.includes(currentDeviceBrand);
  }

  private addAdditionPlans() {
    this.providersRepository.availableProviders.forEach((provider) => {
      const providerData = this.providersRepository.providerSettings[provider];
      const providerPlan = addProviderPlanToSubscriptions(providerData, provider);

      if (!providerPlan) {
        return;
      }

      this.additionPlans.push(
        createSubscriptionEntity(
          providerPlan,
          new Package({
            id: 0,
            startDate: 0,
            endDate: 0,
            title: providerData.title || '',
            description: '',
            image: '',
            images: [],
            priceDescription: '',
            disclaimer: '',
            subscriptionId: 0,
            basePackageId: 0,
            catalogStartDateTime: '',
            playbackEndDateTime: '',
            tags: {},
            mediaType: MediaType.Package,
            externalId: '',
            adult: false,
            metas: {},
            parentalRating: 0,
            type: 0,
          }),
          {
            purchaseStatus: this.providersRepository.purchasedProviders.includes(provider)
              ? KalturaPurchaseStatus.subscriptionPurchased
              : KalturaPurchaseStatus.forPurchase,
          },
        ),
      );
    });
  }

  private async paymentMethodList() {
    const paymentsList = await lastValueFrom(getPaymentMethodList());
    const paymentMethod = paymentsList?.objects?.find((payment) => payment.isDefault) || paymentsList.objects[0];
    this.lastDigits = paymentMethod?.details;
    this.paymentMethodId = paymentMethod?.id;
  }

  public getSubscriptionByPackageId(packageId: number) {
    const allSubscriptions = [...this.heroes, ...this.offers, ...this.subscriptions];
    return allSubscriptions.find(({ packageData }) => packageData.id === packageId);
  }

  @computed get plans() {
    return this.subscriptions.filter(
      (subscription) => subscription.data.dependencyType === 'BASE' && subscription.isPurchased,
    );
  }

  @computed
  public get isAdultEntitled(): boolean {
    const dmsStore = container.resolve<DmsStore>('dmsStore');

    if (
      dmsStore.dms.subscriptionIds?.adults &&
      isTypePurchased(this.subscriptionIds, dmsStore.dms.subscriptionIds?.adults)
    ) {
      return true;
    }
    return false;
  }

  @computed
  public get isUserEntitledToFF(): boolean {
    const dmsStore = container.resolve<DmsStore>('dmsStore');

    if (
      dmsStore.dms.subscriptionIds?.fastForward &&
      isTypePurchased(this.subscriptionIds, dmsStore.dms.subscriptionIds?.fastForward)
    ) {
      return true;
    }
    return false;
  }

  resetStore() {
    this.providersRepository.resetRepository();
    this.additionPlans = [];
  }
}
